// ─── Types ────────────────────────────────────────────────────────────────────

export type RegionId =
  | 'ayia-napa-protaras' | 'larnaca' | 'limassol' | 'paphos' | 'akamas-latchi';

export type DiveType =
  | 'Wreck' |'Reef' |'Cave' |'Wall' |'Artificial Reef' |'Shore';

export type AccessType = 'Shore' | 'Boat' | 'Shore or Boat';

export type SkillLevel = 'Open Water' | 'Advanced' | 'Technical';

export type DepthBand = 'Shallow' | 'Medium' | 'Deep';

export interface DiveSite {
  id: string;
  name: string;
  region: RegionId;
  regionLabel: string;
  diveType: DiveType[];
  access: AccessType;
  minLevel: SkillLevel;
  depthBand: DepthBand;
  maxDepth: string;
  visibility: string;
  gps: string | null;
  description: string;
  highlights: string[];
  marineLife?: string[];
}

// ─── Regions ──────────────────────────────────────────────────────────────────

export const REGIONS: {
  id: RegionId;
  label: string;
  slug: string;
  href: string;
  guideHref: string;
  icon: string;
  tagline: string;
  siteCount?: number;
}[] = [
  {
    id: 'ayia-napa-protaras',
    label: 'Ayia Napa & Protaras',
    slug: 'ayia-napa-protaras',
    href: '/dive-sites/ayia-napa-protaras',
    guideHref: '/diving-regions/ayia-napa-protaras',
    icon: '🐢',
    tagline: 'Crystal-clear shore dives, sea caves & underwater sculpture museum',
  },
  {
    id: 'larnaca',
    label: 'Larnaca',
    slug: 'larnaca',
    href: '/dive-sites/larnaca',
    guideHref: '/diving-regions/larnaca',
    icon: '⚓',
    tagline: 'Home of the legendary MS Zenobia — one of the world\'s top wreck dives',
  },
  {
    id: 'limassol',
    label: 'Limassol',
    slug: 'limassol',
    href: '/dive-sites/limassol',
    guideHref: '/diving-regions/limassol',
    icon: '🌊',
    tagline: 'Cyprus\'s hub for advanced, technical and wall diving',
  },
  {
    id: 'paphos',
    label: 'Paphos',
    slug: 'paphos',
    href: '/dive-sites/paphos',
    guideHref: '/diving-regions/paphos',
    icon: '🏛️',
    tagline: 'History meets the sea — wrecks, caves and beginner shore dives',
  },
  {
    id: 'akamas-latchi',
    label: 'Akamas & Latchi',
    slug: 'akamas-latchi',
    href: '/dive-sites/akamas-latchi',
    guideHref: '/diving-regions/akamas',
    icon: '🌿',
    tagline: 'Pristine wilderness, dramatic drop-offs and exceptional visibility',
  },
];

// ─── Helper ───────────────────────────────────────────────────────────────────

function depthBand(maxM: number): DepthBand {
  if (maxM <= 18) return 'Shallow';
  if (maxM <= 35) return 'Medium';
  return 'Deep';
}

// ─── Dataset ──────────────────────────────────────────────────────────────────

export const diveSites: DiveSite[] = [
  // ── AYIA NAPA & PROTARAS ──────────────────────────────────────────────────

  {
    id: 'green-bay',
    name: 'Green Bay',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Shore'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(11),
    maxDepth: '11m',
    visibility: '20m+ in good conditions',
    gps: "35°0'1.13\"N / 34°4'4.51\"E",
    description:
      'The easiest dive site in the area, ideal for introductory and beginner dives. Very easy shore walk-in with rarely any current or waves. Artificial statues sit at around 10m, and fish feeding takes place at less than 5m.',
    highlights: [
      'Easiest dive site in the area',
      'Ideal for introductory dives and beginners',
      'Artificial statues at around 10m',
      'Frequent turtle sightings',
      'Hundreds of seabreams around the feeding area',
    ],
    marineLife: ['Sea Turtle', 'Seabream', 'Damselfish'],
  },
  {
    id: 'green-bay-cave',
    name: 'Green Bay Cave',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Cave', 'Shore'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(17),
    maxDepth: '17m',
    visibility: '20m+ in good conditions',
    gps: "34°59'57.28\"N / 34°4'23.33\"E",
    description:
      'Rocky bottom with small through-and-through caverns approximately 10m from the entry point. A good DPV / scooter diving site with interesting cavern formations.',
    highlights: [
      'Around 10m swim from entry point',
      'Through-and-through caverns',
      'Good DPV / scooter diving site',
      'Lionfish, turtles and moray eels',
    ],
    marineLife: ['Lionfish', 'Moray Eel', 'Sea Turtle'],
  },
  {
    id: 'cyclopes-caves',
    name: 'Cyclopes Caves',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Cave', 'Wall'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(45),
    maxDepth: '45m',
    visibility: '20m+ in good conditions',
    gps: "34°59'10.24\"N / 34°4'35.99\"E",
    description:
      'One of the most beautiful Cape Greco dive sites. A rocky, sandy bottom leads to a dramatic drop-off cliff. Suitable for everyone from beginners to technical divers, with excellent conditions for mapping, night diving and deep dives.',
    highlights: [
      'One of the most beautiful Cape Greco dive sites',
      'Cliff with moray eels, sea urchins, starfish, lionfish, nudibranchs, groupers and seabreams',
      'Suitable from beginners to technical divers',
      'Good for mapping, night diving, navigation and deep dives',
    ],
    marineLife: ['Moray Eel', 'Grouper', 'Lionfish', 'Nudibranch', 'Starfish', 'Sea Urchin'],
  },
  {
    id: 'chapel-agioi-anargiroi',
    name: 'Chapel / Agioi Anargiroi',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Wall'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(40),
    maxDepth: '40m',
    visibility: '20m+ in good conditions',
    gps: "34°58'33.88\"N / 34°4'33.98\"E",
    description:
      'A scenic dive site below the Agioi Anargiroi chapel. Rocky, sandy bottom with a drop-off cliff. Access is via chapel steps, but entry and exit at around 8m can be challenging.',
    highlights: [
      'Scenic site below Agioi Anargiroi chapel',
      'Good for navigation, deep dives and night dives',
      'Marine life includes moray eels, sea urchins, lionfish, nudibranchs, groupers and seabreams',
    ],
    marineLife: ['Moray Eel', 'Grouper', 'Lionfish', 'Nudibranch', 'Starfish', 'Sea Urchin'],
  },
  {
    id: 'da-costa',
    name: 'Da Costa',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Shore'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(11),
    maxDepth: '11m',
    visibility: '15m+ in good conditions',
    gps: "35°0'24.58\"N / 34°3'44.63\"E",
    description:
      'A beginner-friendly walk-in shore dive with a mostly flat sandy bottom and rocky areas. Rarely affected by waves, making it a reliable site for new divers. Frequent turtle sightings.',
    highlights: [
      'Beginner-friendly walk-in shore dive',
      'Mostly flat sandy bottom with rocky areas',
      'Rarely affected by waves',
      'Frequent turtle sightings',
    ],
    marineLife: ['Sea Turtle', 'Damselfish', 'Wrasse'],
  },
  {
    id: 'caves-protaras',
    name: 'Caves',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Cave'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(12),
    maxDepth: '12m',
    visibility: '20m+ in good conditions',
    gps: "34°57'48.86\"N / 34°4'23.33\"E",
    description:
      'A flat sandy bottom with small through-and-through caverns. Two entry points are available. An easy dive with interesting swim-throughs and cavern-specific marine life.',
    highlights: [
      'Easy dive with interesting caverns',
      'Multiple swim-throughs',
      'Cavern-specific marine life',
      'Access may be difficult for saloon cars',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Nudibranch'],
  },
  {
    id: 'the-canyon',
    name: 'The Canyon',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Wall'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(20),
    maxDepth: '20m',
    visibility: '20m+ in good conditions',
    gps: "34°57'40.41\"N / 34°3'51.72\"E",
    description:
      'A distinct canyon entry with cliffs on both sides. Rocky drop-off to around 18m, then a sandy bottom. More suitable for experienced divers. Boat diving is suggested due to difficult shore access.',
    highlights: [
      'Distinct canyon entry with cliffs on both sides',
      'Rocky drop-off to around 18m, then sandy bottom',
      'More suitable for experienced divers',
      'Frequent stingray sightings',
      'Not suitable for non-4x4 cars',
    ],
    marineLife: ['Stingray', 'Grouper', 'Moray Eel'],
  },
  {
    id: 'gavo-grego-antennas',
    name: 'Gavo Grego Antennas',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Cave', 'Reef'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(16),
    maxDepth: '16m',
    visibility: '20m+ in good conditions',
    gps: "34°57'44.79\"N / 34°4'49.41\"E",
    description:
      'An easy and interesting dive site with a rocky bottom and small caverns. Rich in small marine life. Good for night diving, navigation and skills practice, with easy car access.',
    highlights: [
      'Easy and interesting dive site',
      'Rich small marine life',
      'Good for night diving, navigation and skills practice',
      'Easy access for cars',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Damselfish', 'Nudibranch'],
  },
  {
    id: 'nemesis-iii',
    name: 'Nemesis III Shipwreck',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(26),
    maxDepth: '26m',
    visibility: '20m+ in good conditions',
    gps: "35°3'3.72\"N / 34°2'11.05\"E",
    description:
      'A 25m fishing vessel sunk in 2013 to promote diving. Located just 90m from the Liberty wreck, making it excellent for scooter diving between the two sites. Safe swim-throughs for basic wreck diving.',
    highlights: [
      'Fishing vessel sunk in 2013 to promote diving',
      '25m long and 6m wide',
      'Safe swim-throughs for basic wreck diving',
      'Located 90m from Liberty wreck',
      'Excellent for scooter diving between wrecks',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm'],
  },
  {
    id: 'liberty-shipwreck',
    name: 'Liberty Shipwreck',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(27),
    maxDepth: '27m',
    visibility: '20m+ in good conditions',
    gps: "35°2'59.47\"N / 34°2'11.76\"E",
    description:
      'A Russian cargo ship sunk in 2009, measuring 37m long and 10m wide. Located 90m from the Nemesis III wreck. Safe swim-throughs for basic wreck diving and excellent for scooter diving.',
    highlights: [
      'Russian cargo ship sunk in 2009',
      '37m long and 10m wide',
      'Located 90m from Nemesis III',
      'Safe swim-throughs for basic wreck diving',
      'Excellent for scooter diving',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm'],
  },
  {
    id: 'octopus-dive-site',
    name: 'Octopus Dive Site',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Reef'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(15),
    maxDepth: '15m',
    visibility: '20m+ in good conditions',
    gps: "34°59'27.77\"N / 34°4'30.12\"E",
    description:
      'A beautiful reef site named for the reef channels that resemble octopus tentacles. Good for beginners and advanced divers alike, and suitable for mapping, night diving, navigation and deep dives.',
    highlights: [
      'Beautiful reef site',
      'Named for reef channels resembling octopus tentacles',
      'Good for beginners and advanced divers',
      'Suitable for mapping, night diving, navigation and deep dives',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Lionfish', 'Nudibranch', 'Grouper'],
  },
  {
    id: 'kyrenia-shipwreck',
    name: 'Kyrenia Shipwreck',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(23),
    maxDepth: '23m',
    visibility: '20m+ in good conditions',
    gps: "34°58'42.37\"N / 33°58'52.23\"E",
    description:
      'A Greek navy boat originally called HS Knossos, sunk in 2015 to promote diving. Safe swim-throughs for basic wreck diving. Usually good conditions without strong currents.',
    highlights: [
      'Greek navy boat originally called HS Knossos',
      'Sunk in 2015 to promote diving',
      'Safe swim-throughs for basic wreck diving',
      'Usually good conditions without strong currents',
    ],
    marineLife: ['Grouper', 'Seabream', 'Damselfish', 'Wrasse'],
  },
  {
    id: 'musan',
    name: 'MUSAN – Agia Napa Underwater Sculpture Museum',
    region: 'ayia-napa-protaras',
    regionLabel: 'Ayia Napa & Protaras',
    diveType: ['Artificial Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(23),
    maxDepth: '23m',
    visibility: '20m+',
    gps: null,
    description:
      'An underwater sculpture museum featuring over 90 submerged artworks by Jason deCaires Taylor. Made from inert, pH-neutral materials, the museum is designed for both diving and snorkelling.',
    highlights: [
      'Over 90 submerged artworks',
      'Made from inert, pH-neutral materials',
      'Designed for both diving and snorkelling',
      'Created by Jason deCaires Taylor',
    ],
  },

  // ── LARNACA ───────────────────────────────────────────────────────────────

  {
    id: 'ms-zenobia',
    name: 'MS Zenobia',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(42),
    maxDepth: '42m',
    visibility: '25m+',
    gps: "34°53'31.80\"N / 33°39'16.20\"E",
    description:
      'One of the top 10 wreck dives in the world. A 172m long, 28m wide ferry that sank on its side in 1980. Multiple dives are needed to explore it properly. Suitable for scooter diving.',
    highlights: [
      'One of the top 10 wreck dives in the world',
      '172m long and 28m wide ferry',
      'Sank on its side in 1980',
      'Multiple dives needed to explore properly',
      'Suitable for scooter diving',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm', 'Starfish', 'Sea Turtle'],
  },
  {
    id: 'elpida-shipwreck',
    name: 'Elpida Shipwreck',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(30),
    maxDepth: '30m',
    visibility: '15–25m depending on season',
    gps: "34°57'06.3\"N / 33°41'05.6\"E",
    description:
      'A good-sized wreck suitable for two dives in a day. Safe penetration for certified wreck divers. Starts around 14m and reaches 30m, making it accessible for beginner AOW and experienced divers alike.',
    highlights: [
      'Good size for two dives in a day',
      'Safe penetration for certified wreck divers',
      'Suitable for beginner AOW and experienced divers',
      'Starts around 14m and reaches 30m',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm'],
  },
  {
    id: 'lef1',
    name: 'LEF1',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Wreck', 'Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(13),
    maxDepth: '13m',
    visibility: '15–25m depending on season',
    gps: "34°57'8.23\"N / 33°40'8.50\"E",
    description:
      'A reef stretching parallel with the shore, good for underwater photography and fish identification. A small wreck provides shelter for groupers. Suitable for less experienced divers and snorkellers.',
    highlights: [
      'Reef stretching parallel with shore',
      'Good for underwater photography and fish identification',
      'Small wreck provides shelter for groupers',
      'Suitable for less experienced divers and snorkellers',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Octopus'],
  },
  {
    id: 'hms-cricket',
    name: 'HMS Cricket',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(32),
    maxDepth: '32m',
    visibility: '20m+',
    gps: "34°58'5.40\"N / 33°48'4.02\"E",
    description:
      'A British river gunboat used in WWI and WWII, later used as RAF target practice. The wreck lies upside down with swim-through options for wreck divers.',
    highlights: [
      'British river gunboat used in WWI and WWII',
      'Used as RAF target practice',
      'Upside-down wreck',
      'Swim-through options for wreck divers',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Starfish'],
  },
  {
    id: 'alexandria-shipwreck',
    name: 'Alexandria Shipwreck',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(30),
    maxDepth: '30m',
    visibility: '20m+',
    gps: "N34°53.49 / E33°39.27",
    description:
      'One of the few wooden shipwrecks in Cyprus. A former fishing boat sunk in 2006, measuring 30m long and 9m wide. A small swim-through is available. Convenient depth for technical diving.',
    highlights: [
      'One of the few wooden shipwrecks',
      'Former fishing boat sunk in 2006',
      '30m long and 9m wide',
      'Small swim-through',
      'Convenient depth for technical diving',
    ],
    marineLife: ['Schools of fish'],
  },
  {
    id: 'pyla-pirate-caves',
    name: 'Pyla Pirate Caves / Xylofagou',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Cave'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(5),
    maxDepth: '5m',
    visibility: '20m+ in good conditions',
    gps: "34°56'38.83\"N / 33°51'16.57\"E",
    description:
      'A unique site where divers can surface inside a mountain and see stalactites. Good for experienced Open Water divers seeking a cave and cavern experience. Marine life is not especially rich, but the cave experience is distinctive.',
    highlights: [
      'Good for experienced Open Water divers',
      'Cave and cavern experience',
      'Unique site where divers can surface inside a mountain and see stalactites',
      'Marine life not especially rich, but cave experience is distinctive',
    ],
    marineLife: ['Octopus', 'Damselfish', 'Wrasse', 'Moray Eel'],
  },
  {
    id: 'cape-pyla-reef',
    name: 'Cape Pyla Reef / Xylofagou',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(18),
    maxDepth: '18m',
    visibility: '20m+ in good conditions',
    gps: "34°56'23.67\"N / 33°51'16.22\"E",
    description:
      'A reef with small canyons and rich marine life. The canyons ascend from the bottom, making swim-throughs particularly interesting. Good for experienced Open Water divers.',
    highlights: [
      'Good for experienced Open Water divers',
      'Rich marine life',
      'Small canyons ascending from the bottom',
      'Interesting swim-throughs',
    ],
    marineLife: ['Grouper', 'Moray Eel', 'Octopus', 'Nudibranch'],
  },
  {
    id: 'bullet-point',
    name: 'Bullet Point / Xylofagou',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Reef'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(16),
    maxDepth: '16m',
    visibility: '20m+ in good conditions',
    gps: "34°57'38.79\"N / 33°49'47.84\"E",
    description:
      'An easy dive site with generally good conditions. Located off a shooting range — bullets may be seen on the seabed. Rocky bottom with small canyons.',
    highlights: [
      'Easy dive site with generally good conditions',
      'Located off a shooting range; bullets may be seen on the seabed',
    ],
    marineLife: ['Sea Slug', 'Starfish', 'Moray Eel'],
  },
  {
    id: 'nail-xylofagou',
    name: 'Nail / Xylofagou',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Reef'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(17),
    maxDepth: '17m',
    visibility: '20m+ in good conditions',
    gps: "34°57'27.16\"N / 33°50'9.03\"E",
    description:
      'Close and similar to Bullet Point. Rocky bottom with small caverns. Generally favourable conditions. Shallow and suitable for beginners and snorkellers.',
    highlights: [
      'Close and similar to Bullet Point',
      'Generally favourable conditions',
      'Shallow and suitable for beginners and snorkellers',
    ],
    marineLife: ['Sea Slug', 'Starfish', 'Moray Eel'],
  },
  {
    id: 'dorida-reef',
    name: 'Dorida Reef',
    region: 'larnaca',
    regionLabel: 'Larnaca',
    diveType: ['Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(12),
    maxDepth: '12m',
    visibility: '20m+',
    gps: null,
    description:
      'A shallow reef located approximately 600m from shore near Voroklini, Larnaca. Suitable for snorkellers and leisurely recreational dives.',
    highlights: [
      'Shallow reef suitable for snorkellers',
      'Good for leisurely recreational dives',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Seabream'],
  },

  // ── LIMASSOL ──────────────────────────────────────────────────────────────

  {
    id: 'costandis-shipwreck',
    name: 'Costandis Shipwreck',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(24),
    maxDepth: '24m',
    visibility: '20m+ in good conditions',
    gps: "34°40'46.63\"N / 33°5'6.67\"E",
    description:
      'A USSR trawler built in 1989 and sunk in 2014. One of two Limassol artificial wrecks alongside Lady Thetis. Located 200m from Lady Thetis and good for scooter diving.',
    highlights: [
      'USSR trawler built in 1989 and sunk in 2014',
      'One of two Limassol artificial wrecks with Lady Thetis',
      'Located 200m from Lady Thetis',
      'Good for scooter diving',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm'],
  },
  {
    id: 'lady-thetis-shipwreck',
    name: 'Lady Thetis Shipwreck',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(19),
    maxDepth: '19m',
    visibility: '20m+ in good conditions',
    gps: "34°40'55.78\"N / 33°5'1.99\"E",
    description:
      'A passenger vessel sunk in 2014. Located 200m from Costandis. Easy swim-throughs and penetration options make it accessible for Open Water divers. Good for scooter diving.',
    highlights: [
      'Passenger vessel sunk in 2014',
      'Located 200m from Costandis',
      'Easy swim-throughs and penetration options',
      'Good for scooter diving',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Tube Worm'],
  },
  {
    id: 'the-structures',
    name: 'The Structures',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Artificial Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(22),
    maxDepth: '22m',
    visibility: '15m+ in good conditions',
    gps: "34°40'50.34\"N / 33°5'4.74\"E",
    description:
      'An artificial reef created in 2016/2017, located between Costandis and Lady Thetis. Structures include boulders, tube blocks, trapezes, amphora clusters and carnival masks. Frequent turtle sightings.',
    highlights: [
      'Artificial reef created in 2016/2017',
      'Located between Costandis and Lady Thetis',
      'Structures include boulders, tube blocks, trapezes, amphora clusters and carnival masks',
      'Frequent turtle sightings',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Sea Turtle'],
  },
  {
    id: 'jubilee-shoals',
    name: 'Jubilee Shoals',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Wall', 'Cave'],
    access: 'Boat',
    minLevel: 'Advanced',
    depthBand: depthBand(65),
    maxDepth: '65m',
    visibility: '20m+ in good conditions',
    gps: "34°37'46.44\"N / 32°46'12.78\"E",
    description:
      'The largest natural reef in Cyprus, stretching along the Pissouri coastline. Features a mountain wall, large rock formations and multiple caves. One of Cyprus\'s top cave and wall diving sites, excellent for technical diving.',
    highlights: [
      'Largest natural reef in Cyprus',
      'Stretches along the Pissouri coastline',
      'Mountain wall, large rock formations and multiple caves',
      'One of Cyprus\' top cave and wall diving sites',
      'Excellent for technical diving',
    ],
    marineLife: ['Amberjack', 'Crayfish', 'Wrasse'],
  },
  {
    id: 'three-stars-shipwreck',
    name: 'Three Stars Shipwreck',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(20),
    maxDepth: '20m',
    visibility: '20m+',
    gps: null,
    description:
      'The wreck of the Mav Achaios, built in 1932, which sank in the 1970s after fire and storm damage. Partially submerged near the Akrotiri Peninsula. Rusty colours are especially striking at sunset.',
    highlights: [
      'Wreck of of the Mav Achaios, built in 1932',
      'Sank in the 1970s after fire and storm damage',
      'Partially submerged near Akrotiri Peninsula',
      'Rusty colours are especially striking at sunset',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel'],
  },
  {
    id: 'the-pyramids',
    name: 'The Pyramids',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Artificial Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(35),
    maxDepth: '35m',
    visibility: '20m+',
    gps: null,
    description:
      'Cyprus\'s first and largest artificial reef, covering around 2.5km² near the old harbour of ancient Amathus. Four boulder pyramids and around 100 concrete blocks, with statues adding interest and habitat.',
    highlights: [
      'Cyprus\' first and largest artificial reef, around 2.5km²',
      'Located near the old harbour of ancient Amathus',
      'Four boulder pyramids and around 100 concrete blocks',
      'Statues add interest and habitat',
      'Highest pyramid around 18m tall',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Starfish'],
  },
  {
    id: 'akrotiri-peninsula',
    name: 'Akrotiri Peninsula',
    region: 'limassol',
    regionLabel: 'Limassol',
    diveType: ['Cave', 'Reef', 'Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(20),
    maxDepth: '20m',
    visibility: '20m+',
    gps: null,
    description:
      'A multi-site area including Akrotiri Fish Reserve, Shark Cove, Twin Rocks, Tombs and the Three Stars Wreck. Offers a varied range of diving experiences in one location.',
    highlights: [
      'Area includes Akrotiri Fish Reserve, Shark Cove, Twin Rocks, Tombs and Three Stars Wreck',
      'Offers a varied range of diving experiences',
      'Shark Cove has a submerged tunnel',
      'Twin Rocks includes two rocks protruding from the water and a blowhole',
      'Tombs includes cave formations believed locally to resemble ancient tombs',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Starfish', 'Stingray'],
  },

  // ── PAPHOS ────────────────────────────────────────────────────────────────

  {
    id: 'amphitheatre-paphos',
    name: 'Amphitheatre',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Shore', 'Reef'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(21),
    maxDepth: '21m',
    visibility: '20m+ in good conditions',
    gps: "34°51.351N / 32°20.744E",
    description:
      'Rock formations that resemble an amphitheatre. Suitable for beginners and snorkellers. Mostly flat sandy surface with rocky spots. Easy shore access.',
    highlights: [
      'Rock formations resemble an amphitheatre',
      'Suitable for beginners',
      'Mostly flat sandy surface with rocky spots',
      'Also suitable for snorkellers',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Octopus'],
  },
  {
    id: 'achilleas-shipwreck',
    name: 'Achilleas Shipwreck',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(11),
    maxDepth: '11m',
    visibility: '20m+ in good conditions',
    gps: "34°46'24.42\"N / 32°23'28.20\"E",
    description:
      'A Greek boat built in 1949 that exploded and sank in 1975. Measuring 53m long, it lies on a sandy bottom. A straightforward wreck dive accessible to Open Water divers.',
    highlights: [
      'Greek boat built in 1949',
      'Exploded and sank in 1975',
      '53m long',
      'Lies on sandy bottom',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel'],
  },
  {
    id: 'vera-k-shipwreck',
    name: 'Vera K Shipwreck',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(11),
    maxDepth: '11m',
    visibility: '20m+ in good conditions',
    gps: "34°43'17.28\"N / 32°25'42.18\"E",
    description:
      'A Lebanese freighter that ran aground in 1972 and was sunk in 1974 for safety. Mostly broken up, but archways remain for swim-throughs. Occasional currents, generally safe.',
    highlights: [
      'Lebanese freighter',
      'Ran aground in 1972 and was sunk in 1974 for safety',
      'Mostly broken up, but archways remain for swim-throughs',
      'Occasional currents, generally safe',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Damselfish', 'Wrasse'],
  },
  {
    id: 'amphora-cave',
    name: 'Amphora Cave',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Cave'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(12),
    maxDepth: '12m',
    visibility: '20m+ in good conditions',
    gps: "34°43'21.36\"N / 32°26'14.76\"E",
    description:
      'A beginner-friendly site with a mostly flat sandy bottom and rocky spots. A small cave features a visible amphora. Accessible by boat.',
    highlights: [
      'Beginner-friendly',
      'Mostly flat sandy bottom with rocky spots',
      'Small cave with visible amphora feature',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Damselfish', 'Nudibranch'],
  },
  {
    id: 'pistol-cave',
    name: 'Pistol Cave',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Cave', 'Shore'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(12),
    maxDepth: '12m',
    visibility: '20m+ in good conditions',
    gps: "34°53'1.56\"N / 32°19'51.64\"E",
    description:
      'Small caverns and caves accessible from shore. Suitable for beginners and more experienced divers. Mostly flat sandy surface with rocky spots.',
    highlights: [
      'Small caverns and caves',
      'Suitable for beginners and more experienced divers',
      'Mostly flat sandy surface with rocky spots',
    ],
    marineLife: ['Octopus', 'Moray Eel', 'Damselfish', 'Nudibranch'],
  },
  {
    id: 'white-star-shipwreck',
    name: 'White Star Shipwreck',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Wreck'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(18),
    maxDepth: '18m',
    visibility: '20m+ in good conditions',
    gps: "34°43'56.76\"N / 32°24'51.96\"E",
    description:
      'A Russian fishing boat sunk in 2007, lying on a sandy bottom. Occasional currents, but generally safe. Groupers and seabreams are commonly seen around the wreck.',
    highlights: [
      'Russian fishing boat sunk in 2007',
      'Lies on sandy bottom',
      'Occasional currents, generally safe',
    ],
    marineLife: ['Grouper', 'Seabream'],
  },
  {
    id: 'roman-wall-structures',
    name: 'Roman Wall / Structures',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Artificial Reef', 'Shore'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(4),
    maxDepth: '4m',
    visibility: '10m+ in good conditions',
    gps: "34°45'18.34\"N / 32°24'55.80\"E",
    description:
      'A new artificial reef created in October 2019. Very shallow and suitable for beginners. Easy shore access makes it accessible for all skill levels.',
    highlights: [
      'New artificial reef created in October 2019',
      'Suitable for beginners',
      'Very shallow site',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Octopus'],
  },
  {
    id: 'church-bay',
    name: 'Church Bay',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Reef', 'Cave'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(23),
    maxDepth: '23m',
    visibility: '20m+',
    gps: null,
    description:
      'Located in Coral Bay near Amphitheatre. A rope rail helps guide divers down cliffside access. Gradually increasing depth with reef, rock formations, canyons, gullies, swim-throughs and a cavern.',
    highlights: [
      'Located in Coral Bay near Amphitheatre',
      'Rope rail helps guide divers down cliffside access',
      'Gradually increasing depth',
      'Suitable for beginners',
    ],
    marineLife: ['Grouper', 'Moray Eel', 'Octopus', 'Nudibranch'],
  },
  {
    id: 'manijin-island',
    name: 'Manijin Island',
    region: 'paphos',
    regionLabel: 'Paphos',
    diveType: ['Cave', 'Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(18),
    maxDepth: '18m',
    visibility: '20m+',
    gps: null,
    description:
      'Known for a unique bowl feature created by collapsed rock, entered through an archway. Features 360-degree overhangs and a blowhole exit.',
    highlights: [
      'Known for a unique bowl feature created by collapsed rock',
      'Entered through an archway',
      '360-degree overhangs and blowhole exit',
    ],
    marineLife: ['Diverse marine life'],
  },

  // ── LATCHI / AKAMAS ───────────────────────────────────────────────────────

  {
    id: 'st-georgios-island',
    name: 'St. Georgios Island',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Wall', 'Cave'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(55),
    maxDepth: '55m',
    visibility: '30m+ in good conditions',
    gps: "35°4'29.16\"N / 32°20'0.79\"E",
    description:
      'A mountain wall on one side with a small shallow cave. Very rich marine life and excellent for technical diving. Worth more than one visit to fully explore.',
    highlights: [
      'Mountain wall on one side',
      'Small shallow cave',
      'Excellent for technical diving',
      'Worth more than one visit',
    ],
    marineLife: ['Amberjack', 'Crayfish', 'Wrasse', 'Grouper', 'Starfish', 'Stingray', 'Moray Eel'],
  },
  {
    id: 'st-georgios-reef',
    name: 'St. Georgios Reef',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(10),
    maxDepth: '10m',
    visibility: '30m+ in good conditions',
    gps: "35°4'13.47\"N / 32°19'49.38\"E",
    description:
      'Located opposite St. Georgios Island. An easy dive with good marine life. Rocky bottom with exceptional visibility.',
    highlights: [
      'Located opposite St. Georgios Island',
      'Easy dive',
      'Good marine life site',
    ],
    marineLife: ['Amberjack', 'Crayfish', 'Wrasse', 'Grouper', 'Starfish', 'Stingray', 'Moray Eel'],
  },
  {
    id: 'photiades-wall',
    name: 'Photiades Wall',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Wall'],
    access: 'Shore',
    minLevel: 'Open Water',
    depthBand: depthBand(25),
    maxDepth: '25m',
    visibility: '20m+ in good conditions',
    gps: "35°4'39.10\"N / 32°19'0.26\"E",
    description:
      'A long wall drop-off with potential for a safe drift dive in the right current. Good marine life throughout.',
    highlights: [
      'Long wall drop-off',
      'Potential for a safe drift dive in the right current',
      'Good marine life',
    ],
    marineLife: ['Amberjack', 'Crayfish', 'Wrasse', 'Grouper', 'Starfish', 'Stingray', 'Moray Eel'],
  },
  {
    id: 'blue-lagoon',
    name: 'Blue Lagoon',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Shore'],
    access: 'Shore or Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(10),
    maxDepth: '10m',
    visibility: '20m+ in good conditions',
    gps: "35°4'57.83\"N / 32°18'26.90\"E",
    description:
      'Excellent for beginners. Crystal-clear water with a sandy bottom. A relaxed dive site. Marine life is not especially rich.',
    highlights: [
      'Excellent for beginners',
      'Crystal-clear water',
      'Sandy bottom',
      'Relaxed dive site',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Seabream'],
  },
  {
    id: 'fontana-amorosa',
    name: 'Fontana Amorosa',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Shore'],
    access: 'Shore or Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(15),
    maxDepth: '15m',
    visibility: '20m+ in good conditions',
    gps: "35°5'21.06\"N / 32°18'7.26\"E",
    description:
      'A beginner-friendly site with crystal-clear water and a sandy and rocky bottom. Some marine life around rocky spots. Historically associated with ancient harbour remains and amphorae.',
    highlights: [
      'Beginner-friendly',
      'Crystal-clear water with sandy and rocky bottom',
      'Some marine life around rocky spots',
      'Historically associated with ancient harbour remains and amphorae',
    ],
    marineLife: ['Damselfish', 'Wrasse', 'Octopus', 'Moray Eel'],
  },
  {
    id: 'amphitheatre-bay',
    name: 'Amphitheatre Bay',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Shore'],
    access: 'Shore or Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(18),
    maxDepth: '18m',
    visibility: '20m+ in good conditions',
    gps: "35°4'33.04\"N / 32°19'2.09\"E",
    description:
      'Rock layout resembles an underwater amphitheatre. Beginner-friendly with crystal-clear water and a sandy and rocky bottom.',
    highlights: [
      'Rock layout resembles an underwater amphitheatre',
      'Beginner-friendly',
      'Crystal-clear water',
      'Sandy and rocky bottom',
    ],
    marineLife: ['Grouper', 'Moray Eel', 'Octopus', 'Nudibranch'],
  },
  {
    id: 'aphrodite-rock',
    name: 'Aphrodite Rock',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Reef'],
    access: 'Shore or Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(12),
    maxDepth: '12m',
    visibility: '20m+ in good conditions',
    gps: "35°3'30.30\"N / 32°20'53.16\"E",
    description:
      'A large rock emerging from the seabed. Crystal-clear water with a variety of marine life. Difficult shore access but easy by boat. A relaxed, pleasant dive.',
    highlights: [
      'Large rock emerging from the seabed',
      'Crystal-clear water',
      'Variety of marine life',
      'Relaxed, pleasant dive',
    ],
    marineLife: ['Grouper', 'Lionfish', 'Seabream', 'Moray Eel', 'Starfish'],
  },
  {
    id: 'mazaki-island',
    name: 'Mazaki Island',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Reef'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(22),
    maxDepth: '22m',
    visibility: '20m+ in good conditions',
    gps: "35°6'43.33\"N / 32°16'50.64\"E",
    description:
      'One of the best Akamas sites for marine life. Strong currents are possible and weather can be difficult due to the exposed location. The island barely reaches the surface.',
    highlights: [
      'One of the best Akamas sites for marine life',
      'Strong currents possible',
      'Weather can be difficult due to exposed location',
      'Island barely reaches the surface',
    ],
    marineLife: ['Amberjack', 'Grouper', 'Barracuda', 'Wrasse', 'Moray Eel'],
  },
  {
    id: 'aphrodite-island',
    name: 'Aphrodite Island',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Reef'],
    access: 'Shore or Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(18),
    maxDepth: '18m',
    visibility: '20m+ in good conditions',
    gps: "35°3'30.41\"N / 32°20'54.20\"E",
    description:
      'Located across from the Baths of Aphrodite. A relaxed and safe dive with small swim-through canyons and diverse marine life.',
    highlights: [
      'Across from the Baths of Aphrodite',
      'Relaxed and safe dive',
      'Small swim-through canyons',
    ],
    marineLife: ['Diverse marine life'],
  },
  {
    id: 'saint-george-geronisos',
    name: 'Saint George / Geronisos',
    region: 'akamas-latchi',
    regionLabel: 'Akamas & Latchi',
    diveType: ['Cave', 'Wall'],
    access: 'Boat',
    minLevel: 'Open Water',
    depthBand: depthBand(35),
    maxDepth: '35m',
    visibility: '20m+',
    gps: null,
    description:
      'Also known as Geronisos Island. Features a drop-down mouth onto a 12m ridge, a large swim-through between rocks, and darker corridors with dramatic cave-style passages.',
    highlights: [
      'Also known as Geronisos Island',
      'Drop-down mouth onto a 12m ridge',
      'Large swim-through between rocks',
      'Darker corridors and dramatic cave-style passages',
    ],
    marineLife: ['Amberjack', 'Crayfish', 'Wrasse', 'Grouper', 'Starfish', 'Stingray', 'Moray Eel'],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getSitesByRegion(regionId: RegionId): DiveSite[] {
  return diveSites.filter((s) => s.region === regionId);
}

export function getSiteCount(): number {
  return diveSites.length;
}